import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronRight, IdCard, Crown, Download, FileText, HelpCircle, Info, Lock, LogOut, Pencil, ShieldCheck, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Btn, Modal, Page, useIsDesktop } from "@/components/kit";
import ProfileDesktop from "@/pages/ProfileDesktop";
import { ProfileAvatar } from "@/components/ProfileAvatar";
import { useAuth } from "@/context/auth";
import { useCategories, useMe, useScanUsage, useSubscription } from "@/hooks/queries";
import { api, OWNER_EMAIL } from "@/lib/api";
import { exportCsv } from "@/lib/toto";
import { AvatarCrown, PremiumBadge } from "@/components/PremiumBadge";

const Row = ({ icon: Icon, label, to, onClick, danger, testid, right }) => {
  const cls = `w-full flex items-center gap-3 px-4 h-14 text-left transition-colors hover:bg-[#F8FAFC] ${danger ? "text-[#DC2626]" : ""}`;
  const body = (<><Icon className="w-5 h-5" strokeWidth={1.9} /><span className="flex-1 text-[15px] font-semibold">{label}</span>{right ?? <ChevronRight className="w-5 h-5 text-[#94A3B8]" />}</>);
  return to ? <Link to={to} className={cls} data-testid={testid}>{body}</Link> : <button onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
};

export default function Profile() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const me = useMe().data;
  const usage = useScanUsage().data;
  const sub = useSubscription().data;
  const cats = useCategories().data ?? [];
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const isDesktop = useIsDesktop();
  const isPremium = sub?.planId === "premium" && sub?.status === "active";
  const isOwner = user?.email?.toLowerCase() === OWNER_EMAIL;
  const canAdmin = isOwner && me?.canAccessAdmin === true;
  const canDelete = me?.canDeleteAccount === true;
  const bizCat = cats.find((c) => c.id === user?.businessCategoryId)?.name;

  const onExport = async () => {
    setBusy(true);
    try {
      const list = await api.get("/contacts");
      if (!list.length) return toast("You have no contacts to export yet.");
      exportCsv(list, Object.fromEntries(cats.map((c) => [c.id, c.name])));
      toast.success(`Exported ${list.length} contacts ✓`);
    } catch { toast.error("Could not export contacts. Please try again."); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    setBusy(true);
    try { await api.del("/users/me"); await logout(); nav("/login"); }
    catch (e) { toast.error(e.message || "Could not delete account."); }
    finally { setBusy(false); setModal(null); }
  };

  return (
    <Page className={isDesktop ? "!px-0 !max-w-none" : "pt-4"}>
      {isDesktop ? (
        <ProfileDesktop user={user} me={me} usage={usage} sub={sub} isPremium={isPremium} canAdmin={canAdmin} canDelete={canDelete} bizCat={bizCat} busy={busy}
          onExport={onExport} onLogout={() => setModal("logout")} onDelete={() => setModal("delete")} />
      ) : (<>
      <h1 className="text-[30px] font-extrabold">Profile</h1>
      <div>
        <div>
          <div className="t-card p-5 mt-5 flex items-center gap-4">
            <AvatarCrown show={isPremium}><ProfileAvatar name={user?.name} size={60} /></AvatarCrown>
            <div className="flex-1 min-w-0">
              <p className="text-[19px] font-extrabold truncate" data-testid="profile-name">{user?.name}</p>
              {isPremium && <div className="my-1"><PremiumBadge size="sm" testid="profile-premium-badge" /></div>}
              <p className="text-[14px] text-[#475569] truncate" data-testid="profile-email">{user?.email}</p>
              {user?.phone && <p className="text-[14px] text-[#64748B]" data-testid="profile-phone">{user.phone}</p>}
            </div>
            <Link to="/profile/edit" className="t-iconbtn" data-testid="profile-edit"><Pencil className="w-5 h-5" /></Link>
          </div>
          {(user?.companyName || user?.designation) && (
            <div className="t-card p-4 mt-3 text-[14px] space-y-1" data-testid="profile-business">
              <p><span className="text-[#64748B]">Company: </span><b>{user.companyName}</b></p>
              <p><span className="text-[#64748B]">Designation: </span><b>{user.designation}</b></p>
              {bizCat && <p><span className="text-[#64748B]">Category: </span><b>{bizCat}</b></p>}
            </div>
          )}
          <div className={`rounded-[20px] p-5 mt-5 text-white ${isPremium ? "bg-gradient-to-r from-[#4F46E5] to-[#7C3AED]" : "bg-gradient-to-r from-[#0000EC] to-[#4338CA]"}`}>
            <div className="flex items-center gap-2"><Crown className="w-5 h-5" /><span className="text-[13px] font-semibold text-white/85">Current Plan</span></div>
            <p className="text-[24px] font-extrabold mt-1" data-testid="profile-current-plan">{!sub ? "—" : isPremium ? "Premium" : "Free"}</p>
            <p className="text-[14px] text-white/85" data-testid="profile-scan-usage">Scan usage: {usage?.isPremium ? "Unlimited" : usage ? `${usage.scansUsed} / ${usage.freeScanLimit}` : "—"}</p>
            {!isPremium && <Link to="/subscription" className="mt-4 inline-flex t-btn bg-white text-[#0000EC] h-11 text-[15px]" data-testid="profile-upgrade">Upgrade to Premium</Link>}
            {sub?.isAdminEntitlement && <p className="mt-2 text-[12px] text-white/80" data-testid="profile-admin-premium-note">Premium is included permanently while this account remains the designated admin.</p>}
          </div>
        </div>
        <div>
          <div className="t-card overflow-hidden mt-5 divide-y divide-[#E2E8F0]">
            <Row icon={IdCard} label="Digital Business Card" to="/dbc" testid="profile-dbc" />
            <Row icon={Bell} label="Notifications" to="/notifications/settings" testid="profile-notifications" />
            <Row icon={Target} label="Goals" to="/goals" testid="profile-goals" />
            <Row icon={Download} label={busy ? "Exporting…" : "Export Contacts (CSV)"} onClick={onExport} testid="profile-export" />
            <Row icon={Lock} label="Privacy Policy" to="/legal/privacy" testid="profile-privacy" />
            <Row icon={FileText} label="Terms of Service" to="/legal/terms" testid="profile-terms" />
            <Row icon={HelpCircle} label="Help & Support" to="/legal/support" testid="profile-support" />
            <Row icon={Info} label="About" to="/legal/about" testid="profile-about" />
            {canAdmin && <Row icon={ShieldCheck} label="Admin Panel" to="/admin" testid="profile-admin-panel" />}
          </div>
          <div className="t-card overflow-hidden mt-4 divide-y divide-[#E2E8F0]">
            <Row icon={LogOut} label="Logout" onClick={() => setModal("logout")} testid="profile-logout" />
            {canDelete && <Row icon={Trash2} label="Delete Account" danger onClick={() => setModal("delete")} testid="profile-delete" />}
          </div>
          {me?.canDeleteAccount === false && <p className="mt-2 text-[12px] text-[#64748B]" data-testid="profile-admin-delete-protection">The designated admin account cannot be deleted.</p>}
          <p className="mt-5 text-[13px] text-[#64748B] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#059669]" />Your contacts and card images stay private to your account.</p>
        </div>
      </div>
      </>)}
      <Modal open={modal === "logout"} onClose={() => setModal(null)} title="Log out of TOTO?" testid="logout-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn><Btn onClick={async () => { await logout(); nav("/login"); }} testid="logout-confirm">Logout</Btn></div>
      </Modal>
      <Modal open={modal === "delete"} onClose={() => setModal(null)} title="Delete your account?" description="All your contacts, card images, events and goals will be permanently deleted. This cannot be undone." testid="delete-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn><Btn variant="danger" loading={busy} onClick={onDelete} testid="delete-confirm">Delete</Btn></div>
      </Modal>
    </Page>
  );
}
